import * as React from 'react';
import { MessageCircle } from 'lucide-react';
import { AskMarkdown } from './AskMarkdown';
import { ChatComposer } from './ChatComposer';
import { PastNegotiationSourceModal } from './PastNegotiationSourceModal';
import { detectTextDirection } from '../../utils/textUtils';
import apiService from '../../services/api';
import type { PlaybookRecordPreviewData } from './PlaybookRecordClausePreview';
import {
  buildPlaybookRecordIndex,
  getPlaybookRecord,
  type PlaybookArchiveRow,
  type PlaybookRecordIndex,
} from '../../utils/playbookRecordIndex';
import { handleError } from '../../utils/errorHandling';
import {
  getAskAgentCopy,
  getAskAgentContextSourceAppliedReply,
  formatAskAgentRecommendationAnswer,
  isDeepResearchAffirmative,
  isDeepResearchDecline,
  isSuggestEditRecommendation,
  parseAskAgentContextSourceChoice,
  extractClientRequestFromAskContext,
  extractClausePrefixFromAskContext,
  buildPlaybookReferencesSummaryForSkillRevision,
  type AskAgentContextSource,
  type AskAgentRecommendation,
} from '../../utils/clauseAskAgentContext';
import type { SupportedLanguage } from '../../utils/languageDetection';
import './PlaybookAgentBesidePanel.css';

export type PlaybookAgentChatTurn = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Structured recommendation from the backend, attached to assistant messages. */
  recommendation?: AskAgentRecommendation;
};

export interface PlaybookAgentBesidePanelProps {
  projectLabel: string;
  recordCount: number;
  contextBody: string;
  clauseNumberDisplay?: string | null;
  /** UI strings and auto-sent prompts follow the open clause language. */
  askLanguage?: SupportedLanguage;
  /** Sent automatically once when matched past negotiations exist. */
  initialQuestion?: string | null;
  /** Shown immediately when there are no matched past negotiations (no API call). */
  openingAssistantMessage?: string | null;
  /** User may opt in to deep playbook search after openingAssistantMessage. */
  awaitingDeepResearchConsent?: boolean;
  /** User must pick BoC vs project archive vs both before the first question. */
  awaitingContextSourceChoice?: boolean;
  contextSource?: AskAgentContextSource | null;
  onChooseContextSource?: (source: AskAgentContextSource) => void;
  /** Apply AI track-changes suggestion to the clause client request from chat context. */
  onApplySuggestedEdit?: (messages: PlaybookAgentChatTurn[]) => Promise<void>;
  isApplyingSuggestedEdit?: boolean;
  /** Archive rows in display order (Record 1 … N); enables clickable Record references in answers. */
  playbookRows?: PlaybookArchiveRow[];
  /** Match rank → archive record number for citation link resolution. */
  matchRankToArchiveRecordNum?: Map<number, number>;
  projectId?: string | null;
  playbookDomain?: 'construction-loan' | 'real-estate';
  clauseContextSnippet?: string;
}

type FeedbackSession = {
  targetMessageId: string;
  recommendation: AskAgentRecommendation;
  priorTurns: Array<{ role: 'user' | 'assistant'; content: string }>;
};

export const PlaybookAgentBesidePanel: React.FC<PlaybookAgentBesidePanelProps> = ({
  projectLabel,
  recordCount,
  contextBody,
  clauseNumberDisplay,
  askLanguage = 'he',
  initialQuestion,
  openingAssistantMessage,
  awaitingDeepResearchConsent = false,
  awaitingContextSourceChoice = false,
  contextSource = null,
  onChooseContextSource,
  onApplySuggestedEdit,
  isApplyingSuggestedEdit = false,
  playbookRows = [],
  matchRankToArchiveRecordNum,
  projectId = null,
  playbookDomain = 'construction-loan',
  clauseContextSnippet = '',
}) => {
  const copy = React.useMemo(() => getAskAgentCopy(askLanguage), [askLanguage]);
  const [messages, setMessages] = React.useState<PlaybookAgentChatTurn[]>([]);
  const [sending, setSending] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [deepResearchConsentPending, setDeepResearchConsentPending] = React.useState(
    awaitingDeepResearchConsent,
  );
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const initialQuestionSentRef = React.useRef(false);
  const openingMessageShownRef = React.useRef(false);
  const contextBodyRef = React.useRef(contextBody);
  const contextSourceRef = React.useRef(contextSource);
  React.useEffect(() => {
    contextBodyRef.current = contextBody;
  }, [contextBody]);
  React.useEffect(() => {
    if (contextSource && contextSource !== contextSourceRef.current) {
      initialQuestionSentRef.current = false;
    }
    contextSourceRef.current = contextSource;
  }, [contextSource]);
  const [suggestEditOfferDismissed, setSuggestEditOfferDismissed] = React.useState(false);
  const [suggestEditApplied, setSuggestEditApplied] = React.useState(false);
  const [previewRecordNum, setPreviewRecordNum] = React.useState<number | null>(null);
  const [feedbackDismissedIds, setFeedbackDismissedIds] = React.useState<Set<string>>(
    () => new Set(),
  );
  const [feedbackSession, setFeedbackSession] = React.useState<FeedbackSession | null>(null);
  const [feedbackProcessing, setFeedbackProcessing] = React.useState(false);

  const recordIndex: PlaybookRecordIndex = React.useMemo(
    () => buildPlaybookRecordIndex(playbookRows),
    [playbookRows],
  );

  const validRecordNums = React.useMemo(
    () => new Set(recordIndex.byNumber.keys()),
    [recordIndex],
  );

  const previewCacheRef = React.useRef<Map<string, PlaybookRecordPreviewData>>(new Map());

  const loadRecordPreview = React.useCallback(
    async (row: PlaybookArchiveRow): Promise<PlaybookRecordPreviewData> => {
      const cached = previewCacheRef.current.get(row.id);
      if (cached && !cached.loading) return cached;

      const fallback: PlaybookRecordPreviewData = {
        plainText: row.paragraph_text?.trim() || row.paragraph_preview || null,
        side: row.side === 'our_side' || row.side === 'their_side' ? row.side : null,
        loading: true,
      };
      previewCacheRef.current.set(row.id, fallback);

      if (!projectId) return { ...fallback, loading: false };

      try {
        const res = await apiService.getPastContractsPlaybooksByIds(
          projectId,
          [row.id],
          playbookDomain,
        );
        const hit = res.data?.find((r) => r.id === row.id);
        const side =
          hit?.side === 'our_side' || hit?.side === 'their_side' ? hit.side : fallback.side;
        const data: PlaybookRecordPreviewData = {
          xml: hit?.paragraph_xml?.trim() || null,
          plainText:
            hit?.paragraph_text?.trim() ||
            row.paragraph_text?.trim() ||
            row.paragraph_preview ||
            null,
          side: side ?? null,
          isFinalVersion: hit?.source_doc_type === 'final_version',
          loading: false,
        };
        previewCacheRef.current.set(row.id, data);
        return data;
      } catch {
        const errData: PlaybookRecordPreviewData = {
          ...fallback,
          loading: false,
          error: true,
        };
        previewCacheRef.current.set(row.id, errData);
        return errData;
      }
    },
    [projectId, playbookDomain],
  );

  const recordLinkOpts = React.useMemo(
    () =>
      contextSource !== 'bank_of_changes' &&
      projectId &&
      validRecordNums.size > 0
        ? {
            validRecordNums,
            onRecordClick: (n: number) => setPreviewRecordNum(n),
            getRecord: (n: number) => getPlaybookRecord(recordIndex, n),
            loadPreview: loadRecordPreview,
            matchRankToArchiveRecordNum,
          }
        : undefined,
    [contextSource, projectId, validRecordNums, recordIndex, loadRecordPreview, matchRankToArchiveRecordNum],
  );

  const previewRow =
    previewRecordNum != null ? getPlaybookRecord(recordIndex, previewRecordNum) : undefined;

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleSend = React.useCallback(
    async (apiText: string, userDisplay?: string): Promise<boolean> => {
      const trimmedApi = apiText.trim();
      if (!trimmedApi || sending) return false;
      const display = (userDisplay ?? apiText).trim() || trimmedApi;

      const userMsg: PlaybookAgentChatTurn = { id: `u-${Date.now()}`, role: 'user', content: display };
      setMessages((prev) => [...prev, userMsg]);
      setSending(true);

      const prior = messages.map((m) => ({ role: m.role, content: m.content }));

      try {
        const res = await apiService.playbookAsk(
          contextBodyRef.current,
          trimmedApi,
          prior,
          projectLabel,
          projectId ?? undefined,
          askLanguage,
        );
        if (!res.success || !res.data?.answer) {
          throw new Error(res.message || res.error || 'No answer returned');
        }
        const answer = res.data!.answer;
        const recommendation: AskAgentRecommendation =
          res.data!.recommendation ?? 'unknown';
        const displayContent = formatAskAgentRecommendationAnswer(
          recommendation,
          answer,
          askLanguage,
        );
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: displayContent,
            recommendation,
          },
        ]);
        return true;
      } catch (e) {
        const msg = handleError(e);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: `Sorry — something went wrong: ${msg}`,
          },
        ]);
        return true;
      } finally {
        setSending(false);
      }
    },
    [contextBody, messages, projectLabel, projectId, sending, askLanguage],
  );

  const submitFeedback = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !feedbackSession || !projectId || feedbackProcessing) return;

      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', content: trimmed },
      ]);
      setFeedbackProcessing(true);

      const conversationSnapshot = messages.slice(-8).map((m) => ({
        role: m.role,
        content: m.content.slice(0, 1500),
      }));

      const priorUserTexts = feedbackSession.priorTurns
        .filter((t) => t.role === 'user')
        .map((t) => t.content);
      const combinedFeedback = [...priorUserTexts, trimmed].join('\n\n');

      const targetMessage = messages.find((m) => m.id === feedbackSession.targetMessageId);
      const askAgentExplanation = (targetMessage?.content || '').slice(0, 4000);
      const ctx = contextBodyRef.current;
      const clientRequest = extractClientRequestFromAskContext(ctx).slice(0, 2000);
      const clausePrefix = extractClausePrefixFromAskContext(ctx, 2000);
      const playbookReferencesSummary = buildPlaybookReferencesSummaryForSkillRevision(
        askAgentExplanation,
        playbookRows,
      );

      try {
        const res = await apiService.playbookAskFeedback({
          projectId,
          feedbackText: combinedFeedback,
          language: askLanguage,
          recommendation: feedbackSession.recommendation,
          conversationSnapshot,
          clauseContextSnippet: clausePrefix || clauseContextSnippet || ctx.slice(0, 2000),
          priorFeedbackTurns: feedbackSession.priorTurns,
          clientRequest,
          askAgentExplanation,
          playbookReferencesSummary,
        });

        const data = res.data;
        const assistantText = data?.assistant_message?.trim();
        if (!res.success || !data || !assistantText) {
          throw new Error(res.message || res.error || 'No feedback response');
        }

        if (data.status === 'clarify') {
          setFeedbackSession((prev) =>
            prev
              ? {
                  ...prev,
                  priorTurns: [
                    ...prev.priorTurns,
                    { role: 'user', content: trimmed },
                    { role: 'assistant', content: assistantText },
                  ],
                }
              : prev,
          );
          setMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}`, role: 'assistant', content: assistantText },
          ]);
          return;
        }

        if (data.status === 'rejected') {
          // Keep the feedback session open so the user can retry with clearer input.
          // Reset priorTurns so rejected noise does not pollute the next submission.
          setFeedbackSession((prev) =>
            prev
              ? {
                  ...prev,
                  priorTurns: [],
                }
              : prev,
          );
          setMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}`, role: 'assistant', content: assistantText },
          ]);
          return;
        }

        const versionSuffix =
          data.status === 'accepted' && data.new_version
            ? `\n\n(${copy.feedbackAcceptedVersionNote} ${data.new_version})`
            : '';
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: `${assistantText}${versionSuffix}`,
          },
        ]);
        setFeedbackDismissedIds((prev) => {
          const next = new Set(prev);
          next.add(feedbackSession.targetMessageId);
          return next;
        });
        setFeedbackSession(null);
      } catch (e) {
        const msg = handleError(e);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: `${copy.feedbackProcessingErrorPrefix} ${msg}`,
          },
        ]);
      } finally {
        setFeedbackProcessing(false);
      }
    },
    [
      feedbackSession,
      projectId,
      feedbackProcessing,
      messages,
      askLanguage,
      clauseContextSnippet,
      playbookRows,
      copy,
    ],
  );

  const handleStartFeedback = React.useCallback(
    (target: PlaybookAgentChatTurn) => {
      if (!projectId || !target.recommendation || target.recommendation === 'unknown') return;
      setFeedbackSession({
        targetMessageId: target.id,
        recommendation: target.recommendation,
        priorTurns: [],
      });
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', content: copy.feedbackUserMessageLabel },
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: copy.feedbackPromptReply,
        },
      ]);
    },
    [projectId, copy],
  );

  React.useEffect(() => {
    const notice = openingAssistantMessage?.trim();
    if (!notice || openingMessageShownRef.current || !contextBody.trim()) return;
    openingMessageShownRef.current = true;
    setMessages([{ id: `a-open-${Date.now()}`, role: 'assistant', content: notice }]);
  }, [openingAssistantMessage, contextBody]);

  React.useEffect(() => {
    const q = initialQuestion?.trim();
    if (
      !q ||
      !contextBody.trim() ||
      initialQuestionSentRef.current ||
      awaitingContextSourceChoice
    ) {
      return;
    }
    if (contextSource === 'bank_of_changes' && !contextBody.includes('Bank of Changes option')) {
      return;
    }

    const sendInitial = () => {
      if (initialQuestionSentRef.current) return;
      const body = contextBodyRef.current;
      if (!body.trim()) return;
      if (contextSource === 'bank_of_changes' && !body.includes('Bank of Changes option')) {
        return;
      }
      initialQuestionSentRef.current = true;
      void handleSend(q);
    };

    requestAnimationFrame(() => requestAnimationFrame(sendInitial));
  }, [
    initialQuestion,
    contextBody,
    contextSource,
    handleSend,
    awaitingContextSourceChoice,
  ]);

  const applyContextSourceChoice = React.useCallback(
    (source: AskAgentContextSource, userLabel: string) => {
      if (!onChooseContextSource) return;
      setMessages((prev) => [
        ...prev,
        { id: `u-${Date.now()}`, role: 'user', content: userLabel },
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: getAskAgentContextSourceAppliedReply(source, askLanguage),
        },
      ]);
      onChooseContextSource(source);
    },
    [onChooseContextSource, askLanguage],
  );

  const submitUserMessage = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      if (feedbackSession) {
        await submitFeedback(trimmed);
        return;
      }

      if (awaitingContextSourceChoice && onChooseContextSource) {
        const parsed = parseAskAgentContextSourceChoice(trimmed);
        if (parsed) {
          applyContextSourceChoice(parsed, trimmed);
          return;
        }
        setMessages((prev) => [
          ...prev,
          { id: `u-${Date.now()}`, role: 'user', content: trimmed },
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: copy.contextSourceUnrecognized,
          },
        ]);
        return;
      }

      if (deepResearchConsentPending) {
        if (isDeepResearchDecline(trimmed)) {
          setDeepResearchConsentPending(false);
          setMessages((prev) => [
            ...prev,
            { id: `u-${Date.now()}`, role: 'user', content: trimmed },
            {
              id: `a-${Date.now()}`,
              role: 'assistant',
              content: copy.deepResearchDeclinedReply,
            },
          ]);
          return;
        }
        if (isDeepResearchAffirmative(trimmed)) {
          setDeepResearchConsentPending(false);
          const userLabel =
            trimmed.length <= 30 ? trimmed : copy.deepResearchAffirmativeLabel;
          await handleSend(copy.deepResearchQuestion, userLabel);
          return;
        }
      }

      await handleSend(trimmed);
    },
    [
      awaitingContextSourceChoice,
      onChooseContextSource,
      applyContextSourceChoice,
      deepResearchConsentPending,
      handleSend,
      copy,
      feedbackSession,
      submitFeedback,
    ],
  );

  const lastAssistantMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'assistant') return messages[i];
    }
    return null;
  }, [messages]);

  // Prefer the structured `recommendation` field from the backend. Fall back to
  // the legacy regex parse only when the backend did not attach a recommendation
  // (e.g. an older response or a pre-parsed deep-research opening message).
  const structuredRecommendation: AskAgentRecommendation | undefined =
    lastAssistantMessage?.recommendation;
  const isEditRecommendation = lastAssistantMessage
    ? structuredRecommendation
      ? structuredRecommendation === 'suggest_edit'
      : isSuggestEditRecommendation(lastAssistantMessage.content)
    : false;

  const showSuggestEditOffer =
    Boolean(onApplySuggestedEdit) &&
    Boolean(lastAssistantMessage) &&
    isEditRecommendation &&
    !suggestEditOfferDismissed &&
    !suggestEditApplied &&
    !deepResearchConsentPending &&
    !awaitingContextSourceChoice &&
    !sending &&
    !isApplyingSuggestedEdit &&
    !feedbackSession;

  const feedbackTargetMessage = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (
        m.role === 'assistant' &&
        m.recommendation &&
        m.recommendation !== 'unknown' &&
        !feedbackDismissedIds.has(m.id)
      ) {
        return m;
      }
    }
    return null;
  }, [messages, feedbackDismissedIds]);

  const showFeedbackOffer =
    Boolean(projectId) &&
    Boolean(feedbackTargetMessage) &&
    !feedbackSession &&
    !feedbackProcessing &&
    !deepResearchConsentPending &&
    !awaitingContextSourceChoice &&
    !sending &&
    !isApplyingSuggestedEdit &&
    feedbackTargetMessage?.id === lastAssistantMessage?.id;

  const handleApplySuggestedEdit = React.useCallback(async () => {
    if (!onApplySuggestedEdit || isApplyingSuggestedEdit) return;
    const userMsg: PlaybookAgentChatTurn = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: copy.suggestEditApplyUserMessage,
    };
    let snapshot: PlaybookAgentChatTurn[] = [];
    setMessages((prev) => {
      snapshot = [...prev, userMsg];
      return snapshot;
    });
    try {
      await onApplySuggestedEdit(snapshot);
      setSuggestEditApplied(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: copy.suggestEditAppliedReply,
        },
      ]);
    } catch (e) {
      const msg = handleError(e);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: `${copy.suggestEditApplyErrorPrefix} ${msg}`,
        },
      ]);
    }
  }, [onApplySuggestedEdit, isApplyingSuggestedEdit, copy]);

  const handleDeclineSuggestedEdit = React.useCallback(() => {
    setSuggestEditOfferDismissed(true);
    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', content: copy.suggestEditDeclineLabel },
      {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: copy.suggestEditDeclinedReply,
      },
    ]);
  }, [copy]);

  const inputDisabled =
    !contextBody.trim() || sending || isApplyingSuggestedEdit || feedbackProcessing;
  const showEmpty =
    messages.length === 0 && !sending && !isApplyingSuggestedEdit && !feedbackProcessing;

  return (
    <div className="pba-panel">
      <div className="pba-messages">
        {showEmpty && (
          <div className="pba-empty">
            <MessageCircle className="pba-empty-icon" size={36} aria-hidden />
            <p className="pba-empty-title">Ask about past negotiations</p>
            <p className="pba-empty-hint">
              Context includes the client request, interpretation, matched past negotiations, and
              the playbook for <strong>{projectLabel}</strong> ({recordCount} archive record
              {recordCount === 1 ? '' : 's'}).
              {clauseNumberDisplay ? ` Opened from clause ${clauseNumberDisplay}.` : ''}
            </p>
          </div>
        )}

        {messages.map((m) => {
          const dir = detectTextDirection(m.content);
          const isUser = m.role === 'user';
          return (
            <div key={m.id} className={`pba-bubble pba-bubble-${m.role}`}>
              <div className="pba-bubble-role">{isUser ? 'You' : 'Courtyard'}</div>
              {isUser ? (
                <div
                  className="pba-bubble-content"
                  dir={dir}
                  style={{ textAlign: dir === 'rtl' ? 'right' : 'left' }}
                >
                  {m.content}
                </div>
              ) : (
                <AskMarkdown content={m.content} dir={dir} recordLinks={recordLinkOpts} />
              )}
            </div>
          );
        })}

        {showSuggestEditOffer && (
          <div className="pba-suggest-edit-offer">
            <p
              className="pba-suggest-edit-offer-text"
              dir={askLanguage === 'he' ? 'rtl' : 'ltr'}
            >
              {copy.suggestEditOffer}
            </p>
            <div
              className="pba-quick-replies"
              role="group"
              aria-label={askLanguage === 'he' ? 'עריכה מוצעת' : 'Suggested edit'}
            >
              <button
                type="button"
                className="pba-quick-reply pba-quick-reply-primary"
                disabled={isApplyingSuggestedEdit}
                onClick={() => void handleApplySuggestedEdit()}
              >
                {copy.suggestEditApplyLabel}
              </button>
              <button
                type="button"
                className="pba-quick-reply"
                disabled={isApplyingSuggestedEdit}
                onClick={handleDeclineSuggestedEdit}
              >
                {copy.suggestEditDeclineLabel}
              </button>
              {showFeedbackOffer && feedbackTargetMessage && (
                <button
                  type="button"
                  className="pba-quick-reply"
                  disabled={isApplyingSuggestedEdit}
                  onClick={() => handleStartFeedback(feedbackTargetMessage)}
                >
                  {copy.feedbackButtonLabel}
                </button>
              )}
            </div>
          </div>
        )}

        {showFeedbackOffer && !showSuggestEditOffer && feedbackTargetMessage && (
          <div
            className="pba-quick-replies"
            role="group"
            aria-label={askLanguage === 'he' ? 'פידבק' : 'Feedback'}
          >
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() => handleStartFeedback(feedbackTargetMessage)}
            >
              {copy.feedbackButtonLabel}
            </button>
          </div>
        )}

        {awaitingContextSourceChoice && onChooseContextSource && !sending && !showSuggestEditOffer && (
          <div
            className="pba-quick-replies"
            role="group"
            aria-label={askLanguage === 'he' ? 'בחירת מקור להקשר' : 'Context source'}
          >
            <button
              type="button"
              className="pba-quick-reply pba-quick-reply-primary"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice('bank_of_changes', copy.contextSourceChoiceBank)
              }
            >
              {copy.contextSourceChoiceBank}
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice(
                  'past_negotiations',
                  copy.contextSourceChoiceArchive,
                )
              }
            >
              {copy.contextSourceChoiceArchive}
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() =>
                applyContextSourceChoice('both', copy.contextSourceChoiceBoth)
              }
            >
              {copy.contextSourceChoiceBoth}
            </button>
          </div>
        )}

        {deepResearchConsentPending && !sending && !showSuggestEditOffer && !awaitingContextSourceChoice && (
          <div
            className="pba-quick-replies"
            role="group"
            aria-label={askLanguage === 'he' ? 'תשובות מהירות' : 'Quick replies'}
          >
            <button
              type="button"
              className="pba-quick-reply pba-quick-reply-primary"
              disabled={sending}
              onClick={() => {
                setDeepResearchConsentPending(false);
                void handleSend(copy.deepResearchQuestion, copy.deepResearchAffirmativeLabel);
              }}
            >
              {copy.deepResearchAffirmativeLabel}
            </button>
            <button
              type="button"
              className="pba-quick-reply"
              disabled={sending}
              onClick={() => void submitUserMessage(copy.suggestEditDeclineLabel)}
            >
              {copy.suggestEditDeclineLabel}
            </button>
          </div>
        )}

        {(sending || isApplyingSuggestedEdit || feedbackProcessing) && (
          <div className="pba-bubble pba-bubble-assistant">
            <div className="pba-bubble-role">Courtyard</div>
            <div className="pba-typing" aria-live="polite">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="pba-input-area">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={async ({ text }) => {
            const t = text.trim();
            if (!t || sending || feedbackProcessing) return;
            await submitUserMessage(t);
            setDraft('');
          }}
          disabled={inputDisabled}
          submitting={sending || feedbackProcessing}
          placeholder={
            feedbackSession
              ? copy.feedbackComposerPlaceholder
              : awaitingContextSourceChoice
                ? copy.composerPlaceholderContextSource
                : deepResearchConsentPending
                  ? copy.composerPlaceholderDeepResearch
                  : copy.composerPlaceholderDefault
          }
          layout="sticky"
          followUp={messages.length > 0}
          submitTitle="Send question"
          showAgentModeSelector={false}
        />
      </div>

      {previewRecordNum != null && projectId && previewRow && (
        <PastNegotiationSourceModal
          filename={previewRow.filename?.trim() || 'Source unavailable'}
          filenameDir={detectTextDirection(previewRow.filename || '')}
          projectId={projectId}
          playbookId={previewRow.id}
          searchText={previewRow.paragraph_text?.trim() || previewRow.paragraph_preview}
          domain={playbookDomain}
          onClose={() => setPreviewRecordNum(null)}
        />
      )}
    </div>
  );
};